import { ConfigurationTableSettings, PAGE_SIZES } from 'src/shared/constants/table-settings-constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { Dispatch, useCallback, useMemo } from 'react'
import { Chip, IconButton } from '@mui/material'
import { EditIcon } from 'src/shared/icons/Icons'
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { ConfigurationGridToolbar } from 'src/shared/grid/ConfigurationGridToolbar'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import {
  DataGridDisabledStyled,
  DataGridInactiveRowCssClassId,
} from 'src/shared/styled/data-grid/DataGridDisabledStyled'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'

import { getTranslationForStatus } from 'src/screens/administration/configuration-management/ConfigurationUtils'

interface PrinciplesConfigGridProps {
  tableSettings: ConfigurationTableSettings
  setTableSettings: Dispatch<ConfigurationTableSettings>
}

export const PrinciplesConfigGrid = (props: PrinciplesConfigGridProps) => {
  const { rows, rowsCount, gridLoading, paginationModel } = props.tableSettings
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()

  const handleRowEditClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, params: GridRenderCellParams) => {
      e.stopPropagation()

      const principleId = params.row.id
      const route = ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPrinciplesEdit.params({
        principleId,
      })
      navigate(route)
    },
    [navigate],
  )

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: getMessage('label.name'),
        flex: 2,
        sortable: false,
        filterable: false,
      },
      {
        field: 'status',
        headerName: getMessage('label.status'),
        flex: 0.25,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const statusKey = params.value ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE
          return <Chip label={getTranslationForStatus(getMessage, statusKey)} />
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.25,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <IconButton onClick={(e) => handleRowEditClick(e, params)} color="primary">
              <EditIcon />
            </IconButton>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, handleRowEditClick])

  const handleAddPrincipleClick = useCallback(() => {
    navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPrinciplesCreate.params({}))
  }, [navigate])

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        loading={gridLoading}
        localeText={gridTranslations}
        disableColumnPinning
        disableRowSelectionOnClick
        getRowClassName={(params) => (params.row.status ? '' : DataGridInactiveRowCssClassId)}
        disableColumnMenu
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={(newPagination) => {
          props.setTableSettings({
            ...props.tableSettings,
            paginationModel: newPagination,
          })
        }}
        pageSizeOptions={PAGE_SIZES}
        sx={{
          boxShadow: 'none',
          border: 'none',
          ...DataGridDisabledStyled,
        }}
        slots={{
          toolbar: () => (
            <ConfigurationGridToolbar
              numResults={rowsCount}
              addButtonMessageKey="label.configuration.principles.grid.toolbar.create.button"
              onAddButtonClick={handleAddPrincipleClick}
            />
          ),
        }}
      />
    </DataGridContainer>
  )
}
